import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiActivity, FiShield, FiTrendingUp, FiCheck, FiX, FiStar } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

/**
 * Social proof + value-anchor + testimonial block for the billing page.
 * Sits between the pricing grid and the invoices list, right where a
 * user hesitates before committing. Renders three sections:
 *
 *  1) Trusted-by counters (live from get_public_landing_stats)
 *  2) "Why $49 is a bargain" — anchors against Bloomberg Terminal ($2k/mo)
 *  3) One featured approved testimonial (from public.testimonials)
 *
 * Fails silently on RPC / query errors — social proof should never
 * block the pricing page from rendering.
 */

interface LandingStats {
  analyses: number;
  vetoes: number;
  traders: number;
  capital: number;
}

interface Testimonial {
  id: string;
  user_name: string | null;
  user_email: string;
  type: 'text' | 'video';
  text_content: string | null;
}

const fmt = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-US');
};

const fmtUsd = (n: number): string => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
};

const initialsFromName = (name: string | null, email: string): string => {
  const source = (name && name.trim()) || email.split('@')[0] || 'U';
  const parts = source.replace(/[._-]+/g, ' ').split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

const displayName = (t: Testimonial): string => {
  if (t.user_name && t.user_name.trim()) return t.user_name.trim();
  const [prefix] = t.user_email.split('@');
  return prefix
    .split(/[._-]+/)
    .map(w => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
};

export const BillingSocialProof: React.FC = () => {
  const [stats, setStats] = useState<LandingStats | null>(null);
  const [testimonial, setTestimonial] = useState<Testimonial | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await supabase.rpc('get_public_landing_stats');
        if (mounted && data) setStats(data as LandingStats);
      } catch {
        /* ignore — social proof is non-critical */
      }
      try {
        const { data } = await supabase
          .from('testimonials')
          .select('id, user_name, user_email, type, text_content')
          .eq('status', 'approved')
          .eq('is_featured', true)
          .order('display_order', { ascending: true, nullsFirst: false })
          .limit(1);
        if (mounted && data && data.length > 0) {
          setTestimonial(data[0] as Testimonial);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ── Stats block (all four values pulled live from the RPC) ─────────
  const StatsBlock = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 }}
      className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-xl border border-white/10 rounded-3xl p-8 mb-6"
    >
      <div className="text-center mb-6">
        <div className="text-[10px] font-mono tracking-[0.3em] text-cyan-400 uppercase mb-2">
          ▸ Trusted by traders
        </div>
        <h3 className="text-2xl md:text-3xl font-black text-white">
          Real numbers.{' '}
          <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            Live from the platform.
          </span>
        </h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: FiUsers,      label: 'Traders using the AI', value: stats ? fmt(stats.traders)  : '—', color: 'text-cyan-400' },
          { icon: FiActivity,   label: 'AI analyses run',      value: stats ? fmt(stats.analyses) : '—', color: 'text-purple-400' },
          { icon: FiX,          label: 'Bad trades vetoed',    value: stats ? fmt(stats.vetoes)   : '—', color: 'text-orange-400' },
          { icon: FiShield,     label: 'Capital preserved',    value: stats ? fmtUsd(stats.capital) : '—', color: 'text-green-400' },
        ].map((s, i) => (
          <div key={i} className="text-center p-4 rounded-2xl bg-black/30 border border-white/5">
            <s.icon className={`w-6 h-6 mx-auto mb-2 ${s.color}`} />
            <div className="text-3xl font-black text-white mb-1">{s.value}</div>
            <div className="text-[11px] text-gray-400 uppercase tracking-wide">{s.label}</div>
          </div>
        ))}
      </div>

      {stats && stats.capital > 0 && (
        <div className="mt-6 text-center text-sm text-gray-400">
          <FiCheck className="inline w-4 h-4 text-green-400 mr-1" />
          Every $ preserved is a NO-TRADE verdict we later verified would have lost money.
        </div>
      )}
    </motion.div>
  );

  // ── Value anchor (safe / no branded competitor claims) ────────────
  // Deliberately references the CATEGORY ("institutional trading
  // platforms") rather than a named vendor, and cites a public price
  // RANGE covering Bloomberg, Refinitiv Eikon, FactSet, etc. so the
  // comparison stays truthful, defensible, and lawsuit-proof.
  const ValueAnchorBlock = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.2 }}
      className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-xl border border-white/10 rounded-3xl p-8 mb-6"
    >
      <div className="text-center mb-6">
        <div className="text-[10px] font-mono tracking-[0.3em] text-purple-400 uppercase mb-2">
          ▸ Value anchor
        </div>
        <h3 className="text-2xl md:text-3xl font-black text-white">
          Institutional-grade discipline.{' '}
          <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
            Retail-grade price.
          </span>
        </h3>
        <p className="text-gray-400 mt-2 text-sm max-w-2xl mx-auto">
          Professional traders pay <span className="text-white font-semibold">$1,500 – $2,500 / month</span> for
          institutional data terminals. Snap Chart AI delivers the trader-discipline &amp; post-trade audit layer
          for less than a coffee a day.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {/* Category comparison (unnamed) */}
        <div className="relative p-5 rounded-2xl bg-black/40 border border-red-500/20">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-red-400 mb-1">Institutional platforms</div>
              <div className="text-2xl font-black text-white">$1.5K–2.5K<span className="text-sm text-gray-500">/mo</span></div>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
              <FiTrendingUp className="w-5 h-5 text-red-400" />
            </div>
          </div>
          <ul className="text-xs space-y-1.5 text-gray-400">
            <li className="flex items-center gap-2"><FiCheck className="w-3 h-3 text-gray-500" /> Enterprise data feeds</li>
            <li className="flex items-center gap-2"><FiCheck className="w-3 h-3 text-gray-500" /> News wire &amp; team chat</li>
            <li className="flex items-center gap-2"><FiX className="w-3 h-3 text-red-500" /> Not built for trader psychology</li>
            <li className="flex items-center gap-2"><FiX className="w-3 h-3 text-red-500" /> No AI verdict on your setup</li>
            <li className="flex items-center gap-2"><FiX className="w-3 h-3 text-red-500" /> Requires firm-level licensing</li>
          </ul>
        </div>

        {/* Snap Chart AI column */}
        <div className="relative p-5 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-purple-500/5 border border-cyan-500/30 ring-1 ring-cyan-500/20">
          <div className="absolute -top-2 -right-2 px-2.5 py-0.5 bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-[10px] font-bold rounded-full">
            97% LESS
          </div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-cyan-400 mb-1">Snap Chart AI · Pro Trader</div>
              <div className="text-2xl font-black text-white">$49<span className="text-sm text-gray-500">/mo</span></div>
            </div>
            <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center">
              <FiShield className="w-5 h-5 text-cyan-400" />
            </div>
          </div>
          <ul className="text-xs space-y-1.5 text-gray-300">
            <li className="flex items-center gap-2"><FiCheck className="w-3 h-3 text-green-400" /> AI verdict on every chart you upload</li>
            <li className="flex items-center gap-2"><FiCheck className="w-3 h-3 text-green-400" /> Trader Discipline Index (TDI)</li>
            <li className="flex items-center gap-2"><FiCheck className="w-3 h-3 text-green-400" /> Counterfactual capital tracking</li>
            <li className="flex items-center gap-2"><FiCheck className="w-3 h-3 text-green-400" /> Multi-chart institutional review</li>
            <li className="flex items-center gap-2"><FiCheck className="w-3 h-3 text-green-400" /> Auto-saved audit-ready journal</li>
          </ul>
        </div>
      </div>

      <p className="text-center mt-6 text-xs text-gray-500 italic max-w-2xl mx-auto">
        Not a data-terminal replacement — a discipline-and-audit layer built for the retail trader
        who wants institutional guardrails without the institutional invoice. Price range reflects
        publicly documented professional-terminal pricing (2026).
      </p>
    </motion.div>
  );

  // ── Testimonial (only rendered when there is a real approved one) ─
  const TestimonialBlock = testimonial && (testimonial.text_content?.trim() || testimonial.video_url) ? (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.3 }}
      className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-xl border border-white/10 rounded-3xl p-8 mb-6"
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-black text-lg">
            {initialsFromName(testimonial.user_name, testimonial.user_email)}
          </div>
          <div>
            <div className="flex items-center gap-1">
              {[0, 1, 2, 3, 4].map(i => (
                <FiStar key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <div className="text-white font-bold">{displayName(testimonial)}</div>
            <div className="text-xs text-gray-500">Verified subscriber</div>
          </div>
        </div>
        <blockquote className="text-lg text-gray-200 italic leading-relaxed">
          &ldquo;{testimonial.text_content}&rdquo;
        </blockquote>
      </div>
    </motion.div>
  ) : null;

  return (
    <div className="mb-8">
      {StatsBlock}
      {ValueAnchorBlock}
      {TestimonialBlock}
    </div>
  );
};

export default BillingSocialProof;
